import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { QueryClientProvider } from '@tanstack/react-query';
import { translate } from '~l10n/client';
import activity from 'activity';
import Backbone from 'backbone';
import { delay } from 'underscore';
import user from 'user';

import { toast } from '~shared/components/Toast';
import Demographics from '~teacher/components/Demographics/Demographics';
import { queryClient } from '~teacher/queryClient';
import Constants from 'Constants';
import dots from 'Dots';
import header from 'HeaderController';
import platform from 'Platform';
import teacher from 'TeacherModel';

import { ErrorView } from '../../../shared/error/ErrorView';

let router = null;
const model = teacher.account;
const licenseModel = model.licenseModel;

class DemographicsController {
  doDemo(teacherRouter) {
    router = teacherRouter;
    header.render();

    if (router.isEnteringProfile()) {
      model.resetPasswordFields();
      model.restoreInitialValues();
      model.banner.message = '';
    }

    if (!model.initialEmail) {
      licenseModel.buyerId = user.getId();
      licenseModel.seats = 1;
      licenseModel.years = 1;
      licenseModel.licenseStep = Constants.LICENSE_STEP;
      model.buyingLicense = false;
      model.fetchAccount(() =>
        licenseModel.fetchPlan(() => {
          controller.renderView();
        })
      );
    } else {
      controller.renderView();
    }
  }

  activityRefreshed() {
    controller.determineVisible();
  }

  determineVisible() {
    dots.stop();
    controller.renderView();
  }

  renderView() {
    ReactDOM.render(
      <ErrorView>
        <QueryClientProvider client={queryClient}>
          {Backbone.history.fragment === 'profile/demo' ? (
            <Demographics
              controller={controller}
              model={model}
              selectedTab={Constants.PROFILE_DEMO}
              router={router}
              languageDisabled={activity.isLive()}
            />
          ) : null}
        </QueryClientProvider>
      </ErrorView>,
      document.getElementById('main-content')
    );
  }

  cityChanged(city) {
    model.city = city;
    model.errors.city.error = false;
    controller.determineVisible();
  }

  countryChanged(country) {
    model.country = country;
    model.errors.country.error = false;

    model.zipCode = '';
    model.state = '';

    controller.determineVisible();
  }

  languageChanged(language) {
    model.language = language;
    model.errors.language.error = false;
    controller.determineVisible();
  }

  disabledLanguageClicked(e) {
    model.showLanguageTooltip = true;
    controller.determineVisible();
    controller.addClickListener();
  }

  addClickListener() {
    if (platform.isIos || platform.isIosApp) {
      delay(() => {
        document.addEventListener('touchend', controller.documentClicked);
      });
    } else {
      delay(() => {
        document.addEventListener('click', controller.documentClicked);
      });
    }
  }

  documentClicked() {
    document.removeEventListener('touchend', controller.documentClicked);
    document.removeEventListener('click', controller.documentClicked);
    model.showLanguageTooltip = false;
    controller.determineVisible();
  }

  orgTypeChanged(orgType) {
    model.orgType = orgType;
    model.errors.orgType.error = false;

    controller.determineVisible();
  }

  schoolNameChanged(name) {
    model.schoolName = name;
    model.errors.schoolName.error = false;
    controller.determineVisible();
  }

  zipCodeChanged(zipCode) {
    model.zipCode = zipCode;
    controller.determineVisible();
  }

  stateChanged(state) {
    model.state = state;
    model.errors.state.error = false;
    controller.determineVisible();
  }

  roleChanged(role) {
    model.role = [role];
    model.errors.role.error = false;
    controller.determineVisible();
  }

  orgNameChanged(orgName) {
    model.orgName = orgName;
    model.errors.orgName.error = false;
    controller.determineVisible();
  }

  saveClicked() {
    dots.start('demo-save-button');
    model.validateProfile(controller.saveValidationResult);
  }

  saveValidationResult() {
    if (
      model.errors.firstName.error ||
      model.errors.lastName.error ||
      model.errors.email.error ||
      model.errors.currentPassword.error ||
      model.errors.password.error ||
      model.errors.password2.error
    ) {
      dots.stop();
      router.routeToProfileInfo();
    } else {
      model.trimDemographics();
      if (!model.validateDemographics(false)) {
        dots.stop();
        controller.determineVisible();
      } else {
        model.saveAccount(controller.accountSaved);
      }
    }
  }

  accountSaved(result) {
    if (result.success) {
      user.setFirstName(model.firstName); // Update the user model for the header (SOC-1036).
      licenseModel.setPlanId(model.orgType);

      if (model.language !== user.getLanguage()) {
        user.setLanguage(model.language);
        router.routeToTeacherLogin();
      } else {
        dots.stop();
        toast.positive(translate('Profile updated!'));

        header.render();
        controller.determineVisible();
      }
    } else {
      if (result.oldPasswordWrong) {
        dots.stop();
        router.routeToProfileInfo();
      } else if (result.authFailure) {
        router.routeToTeacherLogin();
      }
      dots.stop();
      toast.critical(translate('Sorry, something unexpected happened.'));
    }
  }
}

const controller = new DemographicsController();
module.exports = controller;
